import { navigateWorkbench, useStudioNavigation, type StudioWorkspaceState } from "../lib/studioNavigation";
import { StudioWorkspaces } from "./StudioWorkspaces";
import { decodePart } from "../lib/base";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { ErrorState, LoadingState } from "../shell/ui";
import { HubFrame } from "./HubFrame";
import { HubAuth } from "./HubAuth";
import { ConfirmEmail, ResetByLink } from "./LinkPages";
import { AgentCatalog } from "./AgentCatalog";
import { AgentCreation } from "./AgentCreation";
import { AgentMissing } from "./AgentMissing";
import { ConsentRefused } from "./ConsentRefused";
import { useAgentReadiness } from "./agentReadiness";
import {
  hub,
  readDeployment,
  announceAccountChange,
  agentHref,
  agentSettingsPage,
  hubSettingsHref,
  type Account,
  type AgentInstance,
  type CreatedAgent,
} from "./api";
import "./hub.css";
import type { Theme } from "../lib/appearance";
import { OwnerHubPages } from "./OwnerHubPages";
import { useAccountCredit } from "./useAccountCredit";
import { OwnerShellContext, ownerPending, type OwnerPending } from "../shell/ownerShell";
import { useHubReadings } from "./useHubReadings";
import { AgentParallel } from "./AgentParallel";
import { LocalDesktopConnect } from "./LocalDesktopConnect";
import { LocalClientsContext, LocalClientsPage } from "./LocalClients";
import type { LocalClient } from "./api";
import { ChannelPage, PublicWorkPage, SquarePage } from "../platform/PublicPages";
import { OwnerPublications } from "../platform/OwnerPublications";
import "../platform/platform.css";

/** The token in a mailed link's address (`#/reset/<token>`), or null when the address is not that link. */
const tokenAfter = (path: string, prefix: string) => path.startsWith(prefix) && path.length > prefix.length ? path.slice(prefix.length) : null;

function creationAddress(hash: string): boolean {
  const [path, search] = hash.split("?");
  const query = new URLSearchParams(search);
  return path === "#/create" || (path === "#/connect" && !query.has("client") && !query.has("desktop"));
}

const isPublicPage = (path: string) => path === "#/square" || path.startsWith("#/channel/") || path.startsWith("#/work/");
// Square stays mounted under a work page so a page keeps its visitor state and
// the viewer comes back to the same place; while hidden, its films let go of
// their sources.
function PublicContent({ path, signedIn = true }: { path: string; signedIn?: boolean }) {
  const squareActive = path === "#/square";
  const [squareOpened, setSquareOpened] = useState(squareActive);
  useEffect(() => { if (squareActive) setSquareOpened(true); }, [squareActive]);
  const rawId = path.slice(path.indexOf("/", 2) + 1);
  // The API rejects malformed IDs.
  const id = decodePart(rawId) ?? rawId;
  return <>
    {squareOpened || squareActive ? <div className="hub-public-content" hidden={!squareActive}>
      <SquarePage active={squareActive} signedIn={signedIn} />
    </div> : null}
    {path.startsWith("#/channel/") ? <div className="hub-public-content"><ChannelPage id={id} /></div>
      : path.startsWith("#/work/") ? <div className="hub-public-content"><PublicWorkPage id={id} /></div> : null}
  </>;
}

export function HubApp({
  account,
  initialAccountVerified = false,
  onAccount,
  theme,
  onTheme,
}: {
  account: Account | null;
  /** The entry already read this account, or received it from authentication. */
  initialAccountVerified?: boolean;
  onAccount: (account: Account | null) => void;
  theme: Theme;
  onTheme: (theme: Theme) => void;
}) {
  const { hash: address, agentId } = useStudioNavigation();
  // An Agent's settings open in the Hub's 设置, beside what only the Hub can
  // change about that Agent; its workspace never draws them.
  const settingsAt = agentId && agentSettingsPage(address) ? hubSettingsHref(agentId, address) : null;
  useEffect(() => { if (settingsAt) navigateWorkbench(settingsAt, true); }, [settingsAt]);
  const hash = settingsAt ? settingsAt.slice(1) : address;
  const workspaceId = settingsAt ? null : agentId;
  const path = hash.split("?")[0];
  const legacyAdmin = path === "#/admin" || path.startsWith("#/admin/");
  useEffect(() => {
    if (legacyAdmin) location.replace(`/admin/${hash.replace("#/admin", "#")}`);
  }, [legacyAdmin, hash]);
  // Guests browse the same app shell. Authentication still goes through the
  // existing Hub login and account state, never a second public-site session.
  useEffect(() => {
    if (account) return;
    const abort = new AbortController();
    const verify = () => { void readDeployment(abort.signal).then(result => {
      if (!abort.signal.aborted && result.kind === "hub" && result.account) onAccount(result.account);
    }).catch(() => {}); };
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("pa:hub:account");
    if (channel) channel.onmessage = verify;
    const visible = () => { if (!document.hidden) verify(); };
    document.addEventListener("visibilitychange", visible);
    return () => { abort.abort(); channel?.close(); document.removeEventListener("visibilitychange", visible); };
  }, [account, onAccount]);
  if (legacyAdmin) return <LoadingState label="正在打开管理后台" />;
  // A link from a mail opens on any browser, signed in or not.
  const resetToken = tokenAfter(path, "#/reset/");
  if (resetToken) return <ResetByLink token={resetToken} account={account} onAccount={onAccount} />;
  const confirmToken = tokenAfter(path, "#/confirm-email/");
  if (confirmToken) return <ConfirmEmail token={confirmToken} />;
  if (!account && isPublicPage(path)) return <HubFrame account={null} agents={[]} active="square" theme={theme} onTheme={onTheme}>
    <PublicContent path={path} signedIn={false} />
  </HubFrame>;
  if (!account)
    return <HubAuth onAccount={onAccount} />;
  return (
    <HubDirectory key={account.id} account={account} initialAccountVerified={initialAccountVerified}
      onAccount={onAccount} theme={theme} onTheme={onTheme} hash={hash} workspaceId={workspaceId} />
  );
}

function HubDirectory({
  account,
  initialAccountVerified,
  onAccount,
  theme,
  onTheme,
  hash,
  workspaceId,
}: {
  account: Account;
  initialAccountVerified: boolean;
  onAccount: (account: Account | null) => void;
  theme: Theme;
  onTheme: (theme: Theme) => void;
  hash: string;
  workspaceId: string | null;
}) {
  const [workspaceStates, setWorkspaceStates] = useState<Record<string, StudioWorkspaceState>>({});
  const workspaceState = useCallback((id: string, state: StudioWorkspaceState) => setWorkspaceStates(old => ({ ...old, [id]: state })), []);
  const [agents, setAgents] = useState<AgentInstance[] | null>(null);
  const [clients, setClients] = useState<LocalClient[] | null>(null);
  const [clientsError, setClientsError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [logoutError, setLogoutError] = useState<unknown>(null);
  const [receipts, setReceipts] = useState<Record<string, CreatedAgent>>({});
  const [creatingAgentId, setCreatingAgentId] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const reload = useCallback(() => setRefresh((n) => n + 1), []);
  // Root's deployment read and authentication responses already establish the
  // account for the first directory read. Consume that proof once; later
  // directory refreshes and every write still revalidate the browser account.
  const entryAccountVerified = useRef(initialAccountVerified);
  const verifyAccount = useCallback(
    async (signal?: AbortSignal) => {
      const current = await readDeployment(signal);
      if (signal?.aborted) return false;
      if (current.kind !== "hub")
        throw new ApiError(
          "账号服务暂不可用",
          "尚未确认当前账号，未提交操作。",
          null,
          503,
        );
      if (current.account?.id !== account.id) {
        onAccount(current.account);
        return false;
      }
      // The same person with a role an admin changed since: 平台管理 comes
      // and goes with it without a reload, and what they were doing goes on.
      if (current.account.role !== account.role) onAccount(current.account);
      return true;
    },
    [account.id, account.role, onAccount],
  );
  useEffect(() => {
    const visible = () => {
      if (document.visibilityState === "visible") reload();
    };
    const channel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel("pa:hub:account");
    if (channel) channel.onmessage = reload;
    document.addEventListener("visibilitychange", visible);
    return () => {
      channel?.close();
      document.removeEventListener("visibilitychange", visible);
    };
  }, [reload]);
  useEffect(() => {
    setCreatingAgentId(id => id && !workspaceId && creationAddress(hash) ? id : null);
  }, [hash, workspaceId]);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const verifiedAtEntry = entryAccountVerified.current && refresh === 0;
        if (!verifiedAtEntry && !(await verifyAccount(controller.signal))) return;
        const [directory, computers] = await Promise.allSettled([
          hub.agents(controller.signal), hub.clients(controller.signal),
        ]);
        if (controller.signal.aborted) return;
        if (computers.status === "fulfilled") { setClients(computers.value); setClientsError(null); }
        else setClientsError(computers.reason);
        if (directory.status === "rejected") throw directory.reason;
        entryAccountVerified.current = false;
        setAgents(directory.value);
        setError(null);
      } catch (failure) {
        if (controller.signal.aborted) return;
        entryAccountVerified.current = false;
        if (failure instanceof ApiError && failure.status === 401) {
          onAccount(null);
          return;
        }
        setError(failure);
      } finally {
        if (!controller.signal.aborted)
          timer = setTimeout(() => {
            if (document.visibilityState === "hidden") schedule();
            else void load();
          }, 5000);
      }
    };
    const schedule = () => {
      timer = setTimeout(() => {
        if (document.visibilityState === "hidden") schedule();
        else void load();
      }, 5000);
    };
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [refresh, onAccount, verifyAccount]);

  const createdAgent = agents?.find(agent => agent.id === creatingAgentId)
    ?? (creatingAgentId ? receipts[creatingAgentId]?.agent : undefined);
  // Connected is not the same as ready to talk: the Agent itself is asked,
  // for as long as its creation flow stays open.
  const readiness = useAgentReadiness(createdAgent?.online ? createdAgent.id : null);
  useEffect(() => {
    if (!createdAgent?.online || createdAgent.desired_running === false || error || readiness.state !== "ready") return;
    if (!creationAddress(location.hash)) return;
    setCreatingAgentId(null);
    navigateWorkbench(agentHref(createdAgent.id, "#/today"));
  }, [createdAgent?.id, createdAgent?.online, createdAgent?.location, createdAgent?.desired_running, error, hash, readiness.state]);

  const path = hash.split("?")[0];
  const connectionQuery = new URLSearchParams(hash.split("?")[1]);
  const authorizationId = connectionQuery.get("client") || connectionQuery.get("desktop");
  const view =
    workspaceId ? "workspace" : path === "#/create"
      ? "create"
      : path === "#/connect" ? "connect"
        : path === "#/clients" ? "clients"
        : isPublicPage(path) ? "square"
        : path === "#/publish" ? "publish"
        : path === "#/parallel" ? "parallel"
        : path === "#/people" ? "people"
        : path === "#/responses" ? "responses"
        : path === "#/settings" ? "settings"
        : path === "#/consent-refused" ? "consent"
        : path === "#/agents" || path.startsWith("#/agent/") ? "agents" : "overview";
  // Creation already owns its new Agent's readiness read. The navigation keeps
  // the other Agents current and begins observing the new one once creation ends.
  const readingPage = view === "overview" || view === "people" || view === "responses" ? view : null;
  const readingState = useHubReadings((agents ?? []).filter(agent => agent.id !== creatingAgentId), readingPage);
  const agentId = !workspaceId && path.startsWith("#/agent/") ? decodePart(path.slice(8)) ?? "invalid" : null;
  const agent = agents?.find((value) => value.id === agentId);
  // Old connection links lead to the same workspace. Only an unpaired local
  // Agent needs its pairing controls, which now live in its settings.
  useEffect(() => {
    if (agent) navigateWorkbench(agent.location === "local" && !agent.client_id && !agent.paired
      ? hubSettingsHref(agent.id) : agentHref(agent.id, "#/today"), true);
  }, [agent]);
  const workspaceAgent = agents?.find(value => value.id === workspaceId);
  const previousWorkspace = useRef<string | null>(null);
  // The bar's default entry means returning to this Agent. Resolve it before
  // messaging its retained frame: routing through today would unmount its
  // current panel and reload its data and media before opening it again.
  const remembered = workspaceAgent && workspaceId !== previousWorkspace.current && hash === "#/today"
    ? workspaceStates[workspaceAgent.id]?.hash : undefined;
  const workspaceHash = remembered?.startsWith("#/") ? remembered : hash;
  useLayoutEffect(() => {
    previousWorkspace.current = workspaceId;
    if (workspaceId && workspaceHash !== hash) navigateWorkbench(agentHref(workspaceId, workspaceHash), true);
  }, [workspaceId, workspaceHash, hash]);
  const logout = () => {
        setLogoutError(null);
        hub
          .logout()
          .then(() => {
            announceAccountChange();
            onAccount(null);
          })
          .catch(setLogoutError);
  };
  // What the sidebar says about the account: what waits on the owner, what is left to spend, and the way out.
  const credit = useAccountCredit(account.id);
  const pendingByAgent = useMemo<Record<string, OwnerPending>>(() => Object.fromEntries((agents ?? []).map(agent => {
    // An open Agent already pushes its confirmed pending summary. Do not run a
    // second fleet-wide bootstrap/approval loop just to draw sidebar badges.
    if (!readingPage) {
      const frame = agent.id === workspaceId ? workspaceStates[agent.id] : undefined;
      const summary = frame?.ready ? frame.pending : undefined;
      if (agent.online && summary?.state === "ready" && Number.isInteger(summary.count) && summary.count! >= 0
          && (summary.count === 0 || summary.href?.startsWith("#/chat?")))
        return [agent.id, summary];
      return [agent.id, { state: summary?.state === "reading" ? "reading" : "unavailable" }];
    }
    const reading = readingState.readings[agent.id];
    const state = !agent.online || reading?.state === "failed" || reading?.approvalsState === "failed"
      || (reading?.state === "ready" && !reading.boot?.owner?.present) ? "unavailable"
      : reading?.state === "ready" && reading.approvalsState === "ready" ? "ready" : "reading";
    return [agent.id, ownerPending(reading?.boot?.panels, reading?.pending, state)];
  })), [agents, readingPage, readingState.readings, workspaceId, workspaceStates]);
  const leave = useRef(logout);
  leave.current = logout;
  const shell = useMemo(() => ({ pendingByAgent, credit, onLogout: () => leave.current() }), [pendingByAgent, credit]);
  return (
    <LocalClientsContext.Provider value={{ clients, error: clientsError, refresh: reload }}>
    <OwnerShellContext.Provider value={shell}>
    <HubFrame
      account={account}
      agents={agents}
      active={view === "clients" ? "settings" : view === "parallel" || view === "consent" ? "agents" : view}
      workspaceAgent={workspaceAgent}
      workspaceState={workspaceId ? workspaceStates[workspaceId] : undefined}
      agentDestinations={Object.fromEntries(Object.entries(workspaceStates).flatMap(([id, state]) =>
        state.hash?.startsWith("#/") ? [[id, agentHref(id, state.hash)]] : []))}
      workspaces={<StudioWorkspaces agents={agents ?? []} current={workspaceAgent?.id ?? null} hash={workspaceHash} onState={workspaceState} />}
      notice={logoutError || error ? <>
      {logoutError ? <ErrorState error={logoutError} /> : null}
      {error && agents ? (
        <div className="hub-refresh-notice" role="status">
          <span>IP 列表暂未更新，当前界面和上次的列表已保留。</span>
          <button type="button" className="btn btn-quiet" onClick={reload}>重试连接</button>
          <details><summary>查看原因</summary><ErrorState error={error} /></details>
        </div>
      ) : error ? (
        <div className="hub-load-error">
          <ErrorState error={error} onRetry={reload} />
        </div>
      ) : null}
      </> : null}
      onLogout={logout}
      theme={theme}
      onTheme={onTheme}
    >
      <PublicContent path={view === "square" ? path : ""} />
      {view === "square" ? null
        : view === "publish" ? <div className="hub-public-content"><OwnerPublications address={hash} /></div>
        : view === "clients" ? <LocalClientsPage />
        : view === "consent" ? <ConsentRefused />
        : view === "workspace" ? (!workspaceAgent && agents ? <AgentMissing /> : null) : view === "create" || view === "connect" ? (
        view === "connect" && authorizationId ? <LocalDesktopConnect requestId={authorizationId}
          accountName={account.name} verifyAccount={verifyAccount} onRefresh={reload} onSwitchAccount={logout} /> : <AgentCreation
          key={view}
          mode={view}
          accountId={account.id}
          accountName={account.name}
          preparing={createdAgent}
          readiness={readiness}
          preparationStale={Boolean(error)}
          onRefresh={reload}
          verifyAccount={verifyAccount}
          onCreated={(receipt, continueSetup) => {
            setAgents((old) => [
              ...(old ?? []).filter((item) => item.id !== receipt.agent.id),
              receipt.agent,
            ]);
            setReceipts((old) => ({ ...old, [receipt.agent.id]: receipt }));
            if (!continueSetup || location.hash.split("?")[0] !== `#/${view}`) { reload(); return; }
            // Creation waits for this Agent to answer, independently of its client heartbeat.
            setCreatingAgentId(receipt.agent.id);
            reload();
          }}
        />
      ) : agent ? (
        <LoadingState label="正在打开" />
      ) : agentId && agents ? (
        <AgentMissing />
      ) : agents ? (
        view === "parallel" ? <AgentParallel agents={agents} />
          : view === "agents" ? <AgentCatalog agents={agents} stale={Boolean(error)} accountId={account.id} />
          : <OwnerHubPages {...readingState} page={view} hash={hash} agents={agents} accountName={account.name} onLogout={logout}
              pairingReceipts={receipts}
              onPairingIssued={receipt => setReceipts(old => ({ ...old, [receipt.agent.id]: receipt }))}
              onAgentUpdated={updated => {
                setAgents(old => old?.map(agent => agent.id === updated.id ? updated : agent) ?? null);
                reload();
              }}
              // The Hub has answered that it is gone: out of the list now, and
              // the next directory read confirms it.
              onAgentDeleted={id => {
                const [currentPage, currentQuery] = location.hash.split("?");
                if (currentPage === "#/settings" && new URLSearchParams(currentQuery).get("agent") === id)
                  navigateWorkbench("/#/settings?section=workspace", true);
                setAgents(old => old?.filter(agent => agent.id !== id) ?? null);
                reload();
              }} />
      ) : !error ? (
        <LoadingState label="正在读取你的 IP" />
      ) : null}
    </HubFrame>
    </OwnerShellContext.Provider>
    </LocalClientsContext.Provider>
  );
}
